import { byId } from "./domUtils";
import type { Root } from "./prototypeTypes";
import { invalidateFeedbackAttachmentUpload } from './feedbackAttachmentUploadState';
import { resetAiCreateChapterPresets } from './aiCreateLaunchGate';

export function setWizardStep(root: Root, prefix: "wizard" | "aiWizard", step: number) {
  const clamped = Math.min(Math.max(step, 1), 3);
  const panelPrefix = prefix === "wizard" ? "wizardPanel" : "aiWizardPanel";
  const stepsId = prefix === "wizard" ? "wizardSteps" : "aiWizardSteps";
  for (let index = 1; index <= 3; index += 1) {
    byId(root, `${panelPrefix}${index}`)?.classList.toggle("active", index === clamped);
  }
  byId(root, stepsId)
    ?.querySelectorAll<HTMLElement>(".wizard-step")
    .forEach((node) => node.classList.toggle("active", node.dataset.step === String(clamped)));
}

/**
 * Reset the Submit Idea wizard modal to its initial state.
 * Clears all input/textarea values, removes dynamic link/attachment rows,
 * resets wizard to step 1, and clears validation errors.
 */
export function resetIdeaWizardForm(root: Root) {
	const submissionHost = root.querySelector<HTMLElement>('#submitIdeaModal');
	if (submissionHost) {
		delete submissionHost.dataset.submissionId;
		delete submissionHost.dataset.submissionDraft;
		delete submissionHost.dataset.submissionFingerprint;
	}
	// Clear text inputs
	const ideaTitle = byId(root, "ideaTitle") as HTMLInputElement | null;
	if (ideaTitle) ideaTitle.value = "";
	const ideaSubtitle = byId(root, "ideaSubtitle") as HTMLInputElement | null;
	if (ideaSubtitle) ideaSubtitle.value = "";

	// Clear textareas
	const ideaStrengths = byId(root, "ideaStrengths") as HTMLTextAreaElement | null;
	if (ideaStrengths) ideaStrengths.value = "";
	const ideaWeaknesses = byId(root, "ideaWeaknesses") as HTMLTextAreaElement | null;
	if (ideaWeaknesses) ideaWeaknesses.value = "";

	// Reset link list to initial state (one empty row)
	const linkList = byId(root, "ideaLinkList");
	if (linkList) {
		const rows = linkList.querySelectorAll(".idea-link-row");
		rows.forEach((row, i) => {
			if (i === 0) {
				row.querySelectorAll("input").forEach((input) => {
					(input as HTMLInputElement).value = "";
				});
			} else {
				row.remove();
			}
		});
	}
	const addLinkBtn = byId(root, "ideaAddLinkBtn") as HTMLElement | null;
	if (addLinkBtn) addLinkBtn.style.display = "";

	// Clear attachment rows
	const attachmentList = byId(root, "ideaAttachmentList");
	if (attachmentList) {
		attachmentList.querySelectorAll(".attachment-row").forEach((row) => row.remove());
	}
	const addAttachBtn = byId(root, "ideaAddAttachmentBtn") as HTMLElement | null;
	if (addAttachBtn) addAttachBtn.style.display = "";

	// Reset wizard to step 1
	setWizardStep(root, "wizard", 1);

	// Clear validation errors on all panels
	for (let i = 1; i <= 3; i++) {
		const panel = byId(root, `wizardPanel${i}`);
		if (panel) {
			panel.querySelectorAll(".wizard-field-error").forEach((el) => el.remove());
			panel.querySelectorAll<HTMLElement>(".input-error").forEach((el) => {
				el.classList.remove("input-error");
				el.removeAttribute('aria-invalid');
				el.removeAttribute('aria-errormessage');
				el.removeAttribute('aria-describedby');
			});
		}
	}
}

/**
 * Reset the AI Create wizard modal to its initial state.
 * Same pattern as resetIdeaWizardForm but for the AI Create prefix.
 */
export function resetAiCreateWizardForm(root: Root, options: { resetSubmitButton?: boolean } = {}) {
	const submissionHost = root.querySelector<HTMLElement>('#aiCreateModal');
	if (submissionHost) {
		delete submissionHost.dataset.idempotencyKey;
		delete submissionHost.dataset.submitting;
		delete submissionHost.dataset.submissionDraft;
		delete submissionHost.dataset.submissionFingerprint;
	}
	if (options.resetSubmitButton !== false) {
		const submitButton = byId(root, 'aiSubmitBtn') as HTMLButtonElement | null;
		if (submitButton) {
			submitButton.disabled = true;
			submitButton.removeAttribute('aria-busy');
		}
	}
	// Clear text inputs
	const aiTopicTitle = byId(root, "aiTopicTitle") as HTMLInputElement | null;
	if (aiTopicTitle) aiTopicTitle.value = "";
	const aiTopicSubtitle = byId(root, "aiTopicSubtitle") as HTMLInputElement | null;
	if (aiTopicSubtitle) aiTopicSubtitle.value = "";
	const aiTopicChapters = byId(root, "aiTopicChapters") as HTMLInputElement | null;
	if (aiTopicChapters) aiTopicChapters.value = "";
	// 重置章节预设到 UI 默认 Auto-Plan，隐藏自定义输入区
	resetAiCreateChapterPresets(root);

	// Clear textareas
	const aiTopicStrengths = byId(root, "aiTopicStrengths") as HTMLTextAreaElement | null;
	if (aiTopicStrengths) aiTopicStrengths.value = "";
	const aiTopicWeaknesses = byId(root, "aiTopicWeaknesses") as HTMLTextAreaElement | null;
	if (aiTopicWeaknesses) aiTopicWeaknesses.value = "";

	// Reset link list to initial state (one empty row)
	const linkList = byId(root, "aiLinkList");
	if (linkList) {
		const rows = linkList.querySelectorAll(".idea-link-row");
		rows.forEach((row, i) => {
			if (i === 0) {
				row.querySelectorAll("input").forEach((input) => {
					(input as HTMLInputElement).value = "";
				});
			} else {
				row.remove();
			}
		});
	}
	const addLinkBtn = byId(root, "aiAddLinkBtn") as HTMLElement | null;
	if (addLinkBtn) addLinkBtn.style.display = "";

	// Clear attachment rows
	const attachmentList = byId(root, "aiAttachmentList");
	if (attachmentList) {
		attachmentList.querySelectorAll(".attachment-row").forEach((row) => row.remove());
	}
	const addAttachBtn = byId(root, "aiAddAttachmentBtn") as HTMLElement | null;
	if (addAttachBtn) addAttachBtn.style.display = "";

	// Reset wizard to step 1
	setWizardStep(root, "aiWizard", 1);

	// Clear validation errors on all panels
	for (let i = 1; i <= 3; i++) {
		const panel = byId(root, `aiWizardPanel${i}`);
		if (panel) {
			panel.querySelectorAll(".wizard-field-error").forEach((el) => el.remove());
			panel.querySelectorAll<HTMLElement>(".input-error").forEach((el) => {
				el.classList.remove("input-error");
				el.removeAttribute('aria-invalid');
				el.removeAttribute('aria-errormessage');
				el.removeAttribute('aria-describedby');
			});
		}
	}
}

export function showFeedbackSuccess(root: Root, isLoggedIn: boolean) {
  const success = byId(root, "feedbackSuccess");
  const formCard = byId(root, "feedbackFormCard");
  if (formCard) {
    formCard.hidden = true;
    formCard.style.display = "none";
  }
  if (success) {
    success.hidden = false;
    success.style.display = "block";
  }
  // Show "View My Feedback" button if user is logged in
  const viewBtn = byId(root, "viewMyFeedbackBtn") as HTMLElement | null;
  if (viewBtn && isLoggedIn) viewBtn.style.display = "";
}

export function resetFeedbackForm(root: Root, options: { presentation?: boolean; resetSubmitButton?: boolean } = {}) {
	if (options.resetSubmitButton !== false) invalidateFeedbackAttachmentUpload(root);
	const submissionHost = root.querySelector<HTMLElement>('#view-feedback');
	if (submissionHost) {
		delete submissionHost.dataset.submissionId;
		delete submissionHost.dataset.submissionDraft;
		delete submissionHost.dataset.submissionFingerprint;
	}
	const success = byId(root, "feedbackSuccess");
	const formCard = byId(root, "feedbackFormCard");
	if (options.presentation !== false) {
		if (formCard) {
			formCard.hidden = false;
			formCard.style.display = "";
		}
		if (success) {
			success.hidden = true;
			success.style.display = "none";
		}
	}
	// Clear form field values so "Send Another" starts fresh
	const subject = byId(root, "feedbackSubject") as HTMLInputElement | null;
	if (subject) subject.value = "";
	const body = byId(root, "feedbackBody") as HTMLTextAreaElement | null;
	if (body) body.value = "";
	// Reset character counter to initial state
	const counter = byId(root, "feedbackCharCount");
	if (counter) counter.textContent = "0 / 2000 characters";
	// Reset category selection to default (no active category)
	root.querySelectorAll<HTMLElement>("[data-cat]").forEach((btn) => btn.classList.remove("active", "selected"));
	// Hide "View My Feedback" button
	const viewBtn = byId(root, "viewMyFeedbackBtn") as HTMLElement | null;
	if (viewBtn) viewBtn.style.display = "none";
	// Reset file upload state
	const preview = byId(root, "feedbackFilePreview");
	if (preview) {
		preview.style.display = "none";
		preview.removeAttribute("data-uploaded-key");
		preview.removeAttribute("data-uploaded-submission-id");
	}
	const fileInput = byId(root, "feedbackFileInput") as HTMLInputElement | null;
	if (fileInput) fileInput.value = "";
	for (const alertId of ['feedbackFormAlert', 'feedbackAttachmentAlert']) {
		const alert = byId(root, alertId);
		if (alert) {
			alert.textContent = '';
			alert.hidden = true;
			alert.removeAttribute('data-feedback-kind');
		}
	}
	for (const fieldId of ['feedbackSubject', 'feedbackBody']) {
		const field = byId(root, fieldId);
		field?.classList.remove('input-error');
		field?.removeAttribute('aria-invalid');
		field?.removeAttribute('aria-errormessage');
	}
}

export function enhanceProfileSettingsRows(root: Root) {
  // Settings rows (Privacy & Data, Sign Out) now carry their
  // data-proto-click inline in the markup, so no runtime wiring is
  // needed. This function is kept as a stable mount hook in case
  // future rows need enhancement.
  void root;
}

/**
 * Field definitions for each canonical auth modal form. Maps a form kind
 * to the input IDs that hold user-entered values, the checkbox IDs
 * that need unchecking, and the alert element IDs that should be
 * hidden. Centralised here so every show/success path clears the same
 * set of fields, mirroring how resetIdeaWizardForm / resetAiCreateWizardForm
 * own their respective forms.
 */
type AuthModalKind = 'login' | 'register' | 'forgot' | 'oauthLink' | 'resetPassword';

const AUTH_FIELD_MAP: Record<AuthModalKind, { inputs: string[]; checkboxes: string[]; alerts: string[] }> = {
	login: { inputs: ['loginEmail', 'loginPassword'], checkboxes: [], alerts: ['loginFormAlert'] },
	register: { inputs: ['regName', 'regEmail', 'regPassword', 'regPasswordConfirm'], checkboxes: [], alerts: ['registerFormAlert'] },
	forgot: { inputs: ['forgotEmail'], checkboxes: [], alerts: ['forgotFormAlert'] },
	oauthLink: { inputs: ['oauthLinkPassword'], checkboxes: [], alerts: ['oauthLinkFormAlert'] },
	resetPassword: { inputs: ['resetNewPassword', 'resetConfirmPassword'], checkboxes: [], alerts: ['resetPasswordFormAlert'] },
};

/**
 * Clear all input values, uncheck checkboxes, and hide inline alerts
 * for a canonical auth modal form. Called on reopen (so the user never
 * sees stale data from a previous attempt) and on successful submit
 * (defense-in-depth, matching the wizard form pattern).
 */
export function clearAuthModalFields(root: Root, kind: AuthModalKind): void {
	const fields = AUTH_FIELD_MAP[kind];
	for (const inputId of fields.inputs) {
		const input = byId(root, inputId) as HTMLInputElement | null;
		if (input) input.value = '';
	}
	for (const checkboxId of fields.checkboxes) {
		const checkbox = byId(root, checkboxId) as HTMLInputElement | null;
		if (checkbox) checkbox.checked = false;
	}
	for (const alertId of fields.alerts) {
		const alert = byId(root, alertId);
		if (alert) {
			alert.textContent = '';
			alert.hidden = true;
		}
	}
	resetAuthModalIntentNote(root, kind);
}

/**
 * Auth intent copy is injected only for a real continuation context. Reset it
 * whenever an auth form is reopened so a previous gate message cannot become
 * a misleading default subtitle on a later ordinary sign-in or sign-up.
 */
export function resetAuthModalIntentNote(root: Root, kind: AuthModalKind): void {
	const noteId = kind === 'login' ? 'loginIntentNote' : kind === 'register' ? 'registerIntentNote' : null;
	if (!noteId) return;
	const note = byId(root, noteId);
	if (!note) return;
	note.textContent = '';
	note.hidden = true;
}

/**
 * Retired generic subtitles are intentionally described as normalized prefixes rather
 * than product copy. That keeps the deleted defaults from accidentally
 * returning through a new entry point while still tolerating punctuation and
 * whitespace variations from an integration.
 */
const RETIRED_AUTH_SUBTITLE_PREFIXES = ['sign in to continue', 'welcome back', 'join free'];

function isRetiredAuthSubtitle(message: string): boolean {
	const normalized = message.trim().toLocaleLowerCase().replace(/[–—]/g, '-').replace(/\s+/g, ' ');
	return !normalized || RETIRED_AUTH_SUBTITLE_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

/**
 * The only path allowed to display a login/register intent subtitle. It
 * fails closed for retired generic defaults, so an ordinary auth entry stays
 * subtitle-free even if an older caller tries to restore one.
 */
export function showAuthModalIntentNote(root: Root, kind: 'login' | 'register', message: string | null | undefined): void {
	if (!message || isRetiredAuthSubtitle(message)) {
		resetAuthModalIntentNote(root, kind);
		return;
	}
	const noteId = kind === 'login' ? 'loginIntentNote' : 'registerIntentNote';
	const note = byId(root, noteId);
	if (!note) return;
	note.textContent = message;
	note.hidden = false;
}

export function nextMonthIso() {
  const date = new Date();
  date.setUTCMonth(date.getUTCMonth() + 1);
  return date.toISOString();
}
